import * as THREE from 'three';
import type { BuildingEntity } from '../gateway/contracts';
import {
  LEGACY_BUILDING_BOUNCE_DURATION_SECONDS,
  legacyBuildingBounceYScale,
  applyLegacyBuildingBounceScale,
} from './LegacyBuildingBounce';
import {
  legacyBuildingReadyFeedbackPosition,
  LegacyWhiteStarBurstLibrary,
} from './LegacyBuildingReadyFeedback';
import type { LegacyWhiteStarBurstEffect } from './LegacyPetPointFeedback';

type ReadyInstancedMesh = THREE.InstancedMesh<
  THREE.BufferGeometry,
  THREE.Material | THREE.Material[]
>;

/** Shared visual-only production-ready feedback for read-only world presentations. */
export class LegacyBuildingReadyEffects {
  private readonly starLibrary = new LegacyWhiteStarBurstLibrary();
  private readonly stars = new Set<LegacyWhiteStarBurstEffect>();
  private readonly bounces = new Map<number, number>();
  private generation = 0;
  private disposed = false;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly entityGroups: () => Iterable<THREE.Group>,
  ) {}

  trigger(buildingId: number): void {
    if (this.disposed) return;
    const target = this.targetPosition(buildingId);
    if (target === undefined) return;
    const generation = this.generation;
    if (!this.bounces.has(buildingId)) this.bounces.set(buildingId, 0);
    void this.starLibrary.create(target).then(
      (effect) => {
        if (this.disposed || generation !== this.generation) {
          effect.dispose();
          return;
        }
        this.stars.add(effect);
        this.scene.add(effect.root);
      },
      () => undefined,
    );
  }

  update(deltaSeconds: number): void {
    for (const effect of this.stars) {
      if (!effect.update(deltaSeconds)) this.stars.delete(effect);
    }
    if (this.bounces.size === 0) return;
    const targets = this.bounceTargets(new Set(this.bounces.keys()));
    for (const [buildingId, elapsed] of this.bounces) {
      const nextElapsed = Math.min(elapsed + deltaSeconds, LEGACY_BUILDING_BOUNCE_DURATION_SECONDS);
      const yScale = legacyBuildingBounceYScale(nextElapsed);
      for (const { mesh, index } of targets.get(buildingId) ?? []) {
        applyLegacyBuildingBounceScale(mesh, index, yScale);
      }
      if (nextElapsed >= LEGACY_BUILDING_BOUNCE_DURATION_SECONDS) {
        this.bounces.delete(buildingId);
      } else {
        this.bounces.set(buildingId, nextElapsed);
      }
    }
  }

  clear(): void {
    this.generation += 1;
    for (const effect of this.stars) effect.dispose();
    this.stars.clear();
    this.bounces.clear();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clear();
    this.starLibrary.dispose();
  }

  private bounceTargets(
    buildingIds: ReadonlySet<number>,
  ): ReadonlyMap<number, readonly Readonly<{ mesh: ReadyInstancedMesh; index: number }>[]> {
    const targets = new Map<number, { mesh: ReadyInstancedMesh; index: number }[]>();
    for (const group of this.entityGroups()) {
      group.traverse((candidate) => {
        if (
          !(candidate instanceof THREE.InstancedMesh) ||
          !candidate.visible ||
          candidate.userData.nordResourceSpinners === true
        )
          return;
        const mesh = candidate as ReadyInstancedMesh;
        const buildings = candidate.userData.nordBuildings as readonly BuildingEntity[] | undefined;
        if (buildings === undefined) return;
        for (let index = 0; index < buildings.length; index += 1) {
          const buildingId = buildings[index]?.id;
          if (buildingId === undefined || !buildingIds.has(buildingId)) continue;
          const buildingTargets = targets.get(buildingId) ?? [];
          buildingTargets.push({ mesh, index });
          targets.set(buildingId, buildingTargets);
        }
      });
    }
    return targets;
  }

  private targetPosition(buildingId: number): THREE.Vector3 | undefined {
    const instanceMatrix = new THREE.Matrix4();
    const worldMatrix = new THREE.Matrix4();
    const worldBounds = new THREE.Box3();
    for (const group of this.entityGroups()) {
      let target: THREE.Vector3 | undefined;
      group.traverse((candidate) => {
        if (
          target !== undefined ||
          !(candidate instanceof THREE.InstancedMesh) ||
          !candidate.visible ||
          candidate.userData.nordBuildingReadyResources === true ||
          candidate.userData.nordResourceSpinners === true
        )
          return;
        const mesh = candidate as ReadyInstancedMesh;
        const buildings = candidate.userData.nordBuildings as readonly BuildingEntity[] | undefined;
        const anchors = candidate.userData.nordBuildingAnchorPositions as
          readonly THREE.Vector3[] | undefined;
        const index = buildings?.findIndex((building) => building.id === buildingId) ?? -1;
        const anchor = anchors?.[index];
        if (index < 0 || anchor === undefined) return;
        mesh.geometry.computeBoundingBox();
        if (mesh.geometry.boundingBox === null) return;
        candidate.updateWorldMatrix(true, false);
        mesh.getMatrixAt(index, instanceMatrix);
        worldMatrix.multiplyMatrices(candidate.matrixWorld, instanceMatrix);
        worldBounds.copy(mesh.geometry.boundingBox).applyMatrix4(worldMatrix);
        target = legacyBuildingReadyFeedbackPosition(anchor, worldBounds);
      });
      if (target !== undefined) return target;
    }
    return undefined;
  }
}
